"use client";

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { navigationLinks, NavigationLink, SubNavigationLink } from '@/lib/constants/navigation';
import { Logo } from "@/components/loaders/Logo";
import { MenuIcon } from '@/components/ui/menu-icon';
import { cn, containerClasses } from "@/lib/utils";
import { ChevronDown } from 'lucide-react';

const motionVariants = {
  menu: {
    closed: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.3, ease: "easeInOut" as const }
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3, ease: "easeInOut" as const }
    }
  },
  submenu: {
    closed: {
      opacity: 0,
      y: -10,
      pointerEvents: "none" as const,
      transition: { duration: 0.2, ease: "easeInOut" as const }
    },
    open: {
      opacity: 1,
      y: 0,
      pointerEvents: "auto" as const,
      transition: { duration: 0.2, ease: "easeInOut" as const }
    }
  }
};

interface NavLinkProps {
  href: string;
  active: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  isMobile?: boolean;
  submenu?: SubNavigationLink[];
}

function NavLink({ href, active, children, onClick, isMobile = false, submenu }: NavLinkProps) {
  // Make sure we have a client-side reference to the link properties
  const [linkProps, setLinkProps] = useState<{underlineColor?: string, className?: string}>({});
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  
  useEffect(() => {
    const link = navigationLinks.find(link => link.href === href);
    setLinkProps({
      underlineColor: link?.underlineColor || "",
      className: link?.className
    });
  }, [href]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsSubmenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const linkClasses = cn(
    "uppercase transition-all duration-200",
    isMobile ? "block py-1 relative" : "relative inline-block group",
    linkProps.className
  );

  const textClasses = cn(
    "uppercase transition-all duration-200 flex items-center gap-1",
    active 
      ? "text-foreground font-bold"
      : "text-muted-foreground hover:text-foreground"
  );

  // Mobile submenu handler - Simplified button structure
  if (isMobile && submenu) {
    return (
      <div className="relative group">
        <button 
          onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
          // Hover background remains on the button
          className={cn(linkClasses, "w-full text-left px-2 rounded group-hover:bg-thistle/30")}
        >
          {/* Single span for content and icon, direct styling for active/hover text color */}
          <span className={cn(
            "uppercase flex items-center gap-1",
            "transition-colors duration-200",
            active 
              ? "text-foreground font-bold" // Active state
              : "text-muted-foreground group-hover:text-foreground" // Normal state + hover text color
          )}>
            {children}
            <ChevronDown className={cn("h-4 w-4 transition-transform", isSubmenuOpen && "rotate-180")} />
          </span>
        </button>

        <AnimatePresence>
          {isSubmenuOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={motionVariants.submenu}
              className="pl-6 mt-1 space-y-1"
            >
              {submenu.map((sublink) => (
                <Link 
                  key={sublink.href}
                  href={sublink.href}
                  onClick={() => { setIsSubmenuOpen(false); if (onClick) onClick(); }}
                  className={cn(
                    "block py-1 transition-colors text-base normal-case px-2 rounded hover:bg-thistle/30",
                    pathname === sublink.href 
                      ? "font-semibold text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {sublink.label}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Desktop with submenu
  if (!isMobile && submenu) {
    return (
      <div className="relative" ref={navRef}>
        <button 
          className={linkClasses}
          onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
        >
          <span className={textClasses}>
            {children}
            <ChevronDown className="h-4 w-4" />
          </span>
          {linkProps.underlineColor && (
            <span className={cn("absolute bottom-0 left-0 w-full h-0.5", linkProps.underlineColor)} />
          )}
        </button>

        <AnimatePresence>
          {isSubmenuOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={motionVariants.submenu}
              className={cn(
                "absolute left-0 min-w-[200px] mt-1 py-2",
                "bg-white shadow-lg rounded-md z-50",
                "border border"
              )}
            >
              {submenu.map((sublink) => (
                <Link 
                  key={sublink.href}
                  href={sublink.href}
                  onClick={() => setIsSubmenuOpen(false)}
                  className={cn(
                    "block px-4 py-2 transition-colors",
                    pathname === sublink.href 
                      ? "font-semibold text-foreground bg-accent"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  )}
                >
                  {sublink.label}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default desktop link without submenu
  if (!isMobile) {
    return (
      <Link href={href} className={linkClasses}>
        <span className={textClasses}>
          {children}
        </span>
        {linkProps.underlineColor && (
          <span className={cn("absolute bottom-0 left-0 w-full h-0.5", linkProps.underlineColor)} />
        )}
      </Link>
    );
  }

  // Default mobile link without submenu - Simplified again to single span for consistent hover
  return (
    <Link 
      href={href}
      onClick={onClick}
      // Added 'group' directly, changed to hover:bg-thistle/30
      className={cn(linkClasses, "px-2 rounded group hover:bg-thistle/30")}
    >
      {/* Single span for text, still uses group-hover for text color */}
      <span className={cn(
        "uppercase block",
        "transition-colors duration-200",
        active 
          ? "text-foreground font-bold" // Active state
          : "text-muted-foreground group-hover:text-foreground" // Normal + hover text color
      )}>
        {children}
      </span>
    </Link>
  );
}

export function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [clientLinks, setClientLinks] = useState<NavigationLink[]>([]);
  
  // Ensure navigation links are loaded on the client side to prevent hydration mismatch
  useEffect(() => {
    setClientLinks(navigationLinks);
  }, []);

  const handleScroll = useCallback(() => {
    const isScrolled = window.scrollY > 50;
    setScrolled(isScrolled);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <>
      <header 
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out",
          scrolled || isOpen ? "bg-white/90 shadow-md" : "bg-transparent",
          "backdrop-blur-sm"
        )}
        role="banner"
      >
        <div 
          className={cn(
            "absolute inset-0",
            "transition-colors duration-300",
            scrolled || isOpen ? "bg-white shadow-neumorphic" : "bg-transparent"
          )}
        />
        
        <div className={cn("relative z-10", containerClasses.replace("container mx-auto", ""))}>
          <nav className="flex justify-between items-center h-20" role="navigation">
            <Link href="/" className="flex items-center -ml-1" aria-label="Home">
              <Logo />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-12">
              {clientLinks.map((link) => (
                <NavLink 
                  key={link.href}
                  href={link.href} 
                  active={pathname === link.href || !!(link.submenu && link.submenu.some(sublink => pathname === sublink.href))}
                  submenu={link.submenu}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "md:hidden p-2 rounded-full",
                "hover:bg-white/20 transition-colors",
                "relative z-50"
              )}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              <MenuIcon isOpen={isOpen} />
            </button>
          </nav>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial="closed"
                animate="open"
                exit="closed"
                variants={motionVariants.menu}
                className="fixed left-0 w-full bg-background shadow-md md:hidden z-50 top-[var(--header-height)]"
              >
                <nav className="px-4 py-4 space-y-1">
                  {clientLinks.map((link) => (
                    <NavLink 
                      key={link.href}
                      href={link.href} 
                      active={pathname === link.href || !!(link.submenu && link.submenu.some(sublink => pathname === sublink.href))}
                      onClick={() => setIsOpen(false)}
                      isMobile
                      submenu={link.submenu}
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>
    </>
  );
}